import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useGetAssignment } from '@repo/api/assignment/useGetAssignment';
import { useGetAssignmentSubmissions } from '@repo/api/assignment/useGetAssignmentSubmissions';
import { useSubmitAssignment } from '@repo/api/assignment/useSubmitAssignment';
import { type AssignmentSubmissionSchema, assignmentSubmissionSchema } from '@repo/schemas/assignment/assignmentSubmission';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';

import { useCredentials } from '@/store/auth-store';
import { formErrorToast } from '@/utils/form/formErrorToast';

import { useAssignmentResolveDraft } from './useAssignmentResolveDraft';
import { ASSIGNMENT_RESOLVE_EMPTY_VALUES } from './useAssignmentResolveForm.type';

function useAssignmentIdParam(): number | undefined {
  const { assignmentId: assignmentIdParam } = useParams<{
    assignmentId?: string;
  }>();
  if (!assignmentIdParam) return undefined;
  const parsed = Number(assignmentIdParam);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function useAssignmentResolveForm() {
  const navigate = useNavigate();
  const { id: currentUserId } = useCredentials();
  const assignmentId = useAssignmentIdParam();

  const assignmentQuery = useGetAssignment(assignmentId);
  const submissionsQuery = useGetAssignmentSubmissions(assignmentId);

  const ownSubmissionsCount = useMemo<number>(() => submissionsQuery.data?.filter((entry) => entry.userId === currentUserId).length ?? 0, [submissionsQuery.data, currentUserId]);

  const form = useForm<AssignmentSubmissionSchema>({
    defaultValues: ASSIGNMENT_RESOLVE_EMPTY_VALUES,
    mode: 'onChange',
    resolver: standardSchemaResolver(assignmentSubmissionSchema),
  });

  const { attachments, setAttachments, isDraftReady, clearDraft } = useAssignmentResolveDraft({
    draftKey: `assignment-resolve-draft:${assignmentId ?? 'none'}:${currentUserId}`,
    form,
  });

  const submitAssignment = useSubmitAssignment();

  const assignment = assignmentQuery.data;
  const maxAttempts: number = assignment?.maxAttempts ?? 0;
  const attemptsLeft: number = maxAttempts === 0 ? Infinity : Math.max(0, maxAttempts - ownSubmissionsCount);
  const canSubmit: boolean = attemptsLeft > 0;
  const isQuizAssignment: boolean = assignment?.quiz != null;

  const onSubmit = (data: AssignmentSubmissionSchema) => {
    if (assignmentId === undefined) return;
    if (!canSubmit || isQuizAssignment) return;

    submitAssignment.mutate(
      {
        assignmentId,
        data: { body: data.body },
        attachments: attachments.length > 0 ? attachments : undefined,
      },
      {
        onSuccess: () => {
          clearDraft();
          navigate(`/assignments/${assignmentId}`);
        },
      }
    );
  };

  const onInvalid = () => {
    formErrorToast(form, assignmentSubmissionSchema);
  };

  const isPending = submitAssignment.isPending;
  const isLoadingRemote = !assignmentQuery.isSuccess || !submissionsQuery.isSuccess || !isDraftReady;
  const submitError = submitAssignment.isError ? submitAssignment.errorMessage : null;

  return {
    assignmentId,
    assignment,
    assignmentQuery,
    form,
    attachments,
    setAttachments,
    canSubmit,
    isQuizAssignment,
    attemptsLeft,
    usedAttempts: ownSubmissionsCount,
    onSubmit,
    onInvalid,
    isPending,
    isLoadingRemote,
    submitError,
  };
}
